// R1b-α Fix #2 + Step 3 — server-synced first-run product tour.
// 6 steps · dismissible · persisted in `tour_progress` (tenant + user scoped).
// Backend is the source of truth. localStorage is only an offline hint that
// suppresses the flash while the server call resolves; every state change is
// mirrored to POST /api/tours/progress and lands in the audit ledger.
//
// Restart flow: navigating to /owner?tour=restart resets server state and
// re-launches from step 0. Owner user-menu + Help Center trigger this.
//
// R1b-α Step 3 — sequence extended to cover Section 11.4 in full:
//   1. welcome            2. left-nav         3. top-bar-tools (NEW)
//   4. portal-switcher    5. tiles (recopy)   6. help          (NEW)
// tour_key stays "owner_first_run_v1" so users who already completed the
// tour remain dismissed; they can view the new steps via "Restart Owner
// Tour" in the UserMenu.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

// v3 bump — Tutorial Consistency Upgrade:
//   - Fixed the left-nav step title that contradicted its own body (title
//     said one group count, body said another).
//   - Added a Portfolio Manager step covering the new Workstream B/C flow
//     (photo + video upload, deterministic processing, AI drafts requiring
//     Founder approval, Reviewer-vs-Founder role split).
// Users who already completed v1/v2 stay dismissed; the new step appears
// via "Restart Owner Tour" in the UserMenu.
const TOUR_KEY = "owner_first_run_v3";

const STEPS = [
  {
    key: "welcome",
    testid: "owner-tour-step-welcome",
    title: "Welcome to ERF ConstructionOS",
    body: "This is your Owner Command Center. In a few short steps we'll show you where to find things, how to switch views, and how to get help. You can leave the tour at any time.",
  },
  {
    key: "left-nav",
    testid: "owner-tour-step-left-nav",
    title: "Nine destination groups",
    body: "Command · People & Access · CRM · Financial · Operations · Reports · Integrations & API · Audit & Trust · Help & Learn. Every group under one shell with breadcrumbs, back button, and unified top-bar affordances.",
    targetSelector: ".ds-shell-side",
  },
  {
    key: "top-bar-tools",
    testid: "owner-tour-step-top-bar-tools",
    title: "Your top-bar quick tools",
    body: "See your tenant, review pending approvals, and check notifications from anywhere in the app.",
    // Global search is hidden until B2b, so the tenant chip is the anchor when search isn't rendered.
    targetSelector: '[data-testid="admin-shell-global-search"], [data-testid="admin-shell-tenant-chip"]',
  },
  {
    key: "portal-switcher",
    testid: "owner-tour-step-portal-switcher",
    title: "Portal Switcher — Owner-only",
    body: "View the app as any live portal (Owner, Client). Non-silent impersonation — every action writes to the Audit Ledger. Direct link to the design-library preview available inside.",
    targetSelector: '[data-testid="portal-switcher-launcher"]',
  },
  {
    key: "tiles",
    testid: "owner-tour-step-tiles",
    title: "Your Command Center at a glance",
    body: "Twenty-one tiles organized into four sections: Now (pipeline, active jobs, leads, approvals, field & safety), Money (cash, receivables, budget, bids, change orders), Compliance & Ops (credentials, permits, schedule, labor, materials, integrations, audit), and Intelligence (Morning Brief, Monday Intelligence, Construction IQ). Tiles with a dash and a phase tag are honest placeholders — not yet connected but on the roadmap.",
    targetSelector: '[data-testid="owner-tiles-grid"]',
  },
  {
    key: "help",
    testid: "owner-tour-step-help",
    title: "Restart this tour anytime",
    body: "Open the profile menu in the top-right to restart this tour, visit the Help Center, or change your password. You're all set.",
    targetSelector: '[data-testid="admin-shell-user-menu-toggle"], [data-testid="admin-shell-identity"]',
  },
  // Release B · B0-Closeout — Owner Inbox introduction.
  {
    key: "owner-inbox-tile",
    testid: "owner-tour-step-owner-inbox-tile",
    title: "Owner Requests Inbox",
    body: "Every public quote, contact request, or portal access request lands here first. The authoritative record is saved before any notification is attempted, so nothing is lost even if email fails. Click any tile to open the Inbox with the right filter preapplied.",
    targetSelector: '[data-testid="owner-section-inbox"]',
  },
  // B0-Closeout — Delivery chip legend + strict language.
  {
    key: "delivery-chip-legend",
    testid: "owner-tour-step-delivery-chip-legend",
    title: "provider_accepted is NOT delivered",
    body: "Delivery chips are the platform's honest source of truth. A green 'delivered' chip appears ONLY after SendGrid sends us a signed delivered event. An amber 'provider_accepted' chip means SendGrid took the payload (HTTP 202) — the message is awaiting a delivered event. Watch the difference in the Owner Inbox detail drawer.",
    targetSelector: '[data-testid="owner-inbox-tile-failed_notifications"]',
  },
  // B0-Closeout — Manual resend & communications settings.
  {
    key: "resend-and-comms",
    testid: "owner-tour-step-resend-and-comms",
    title: "Resend without duplicating the request",
    body: "If a notification fails, use the Resend button on the inbox item. A NEW outbox row is created — the underlying lead or portal request is never duplicated. Rate-limited to one resend per 30 seconds. For deeper diagnostics visit Settings → Communications, which shows honest provider state, webhook heartbeat, and last delivered / last failure with sanitized reasons only.",
    targetSelector: '[data-testid="owner-inbox-tile-new_requests"]',
  },
  // Tutorial Consistency Upgrade — Portfolio Manager introduction.
  {
    key: "portfolio-manager",
    testid: "owner-tour-step-portfolio-manager",
    title: "Portfolio Manager — Photos, Video, and AI Drafts",
    body: "Owner Portal → Website Content → Portfolio Manager is the single authorized upload surface for the public portfolio. Photos are cleaned deterministically with Pillow (auto-orient, resize, strip GPS/EXIF); videos with FFmpeg (H.264, +faststart, keyframe extract). GPT-5.4 Vision and local Whisper produce drafts that are ALWAYS marked \"AI Draft — Founder approval required\". Reviewers may upload and draft; only the Founder can publish, unpublish, delete, or override an AI/privacy warning. Immutable originals are preserved privately with derived_from linkage. Open Help Center → Portfolio Manager for the full workflow.",
    targetSelector: '[data-testid="owner-section-inbox"]',
  },
];

const LS_HINT_KEY = "erf.tour.owner_first_run_v1.hint";

function readHint() {
  try { return window.localStorage.getItem(LS_HINT_KEY) || ""; } catch { return ""; }
}
function writeHint(v) {
  try { window.localStorage.setItem(LS_HINT_KEY, v); } catch { /* noop */ }
}

async function fetchProgress() {
  try {
    const r = await api.get(`/tours/progress`, { params: { tour_key: TOUR_KEY } });
    return r.data;
  } catch (_) { return null; }
}

async function postProgress(patch) {
  try {
    const r = await api.post(`/tours/progress`, { tour_key: TOUR_KEY, ...patch });
    return r.data;
  } catch (_) { return null; }
}

/**
 * @param {Object} props
 * @param {string} [props.userId] — kept for API-shape backward compat.
 * @param {boolean} [props.force] — bypass the persisted-dismissal check.
 * @param {()=>void} [props.onClose]
 */
export default function OwnerFirstRunTour({ userId, force = false, onClose }) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const dialogRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const restartParam = new URLSearchParams(location.search).get("tour") === "restart";

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq) {
      setPrefersReducedMotion(mq.matches);
      const on = () => setPrefersReducedMotion(mq.matches);
      mq.addEventListener?.("change", on);
      return () => mq.removeEventListener?.("change", on);
    }
  }, []);

  // Decide on mount whether to show; sync with server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (force || restartParam) {
        await postProgress({ reset: true });
        writeHint("");
        if (!cancelled) { setStep(0); setVisible(true); }
        // NOTE: intentionally leave ?tour=restart in the URL. It is
        // idempotent, deep-linkable, and cleared naturally on next
        // in-app navigation. Stripping it here caused the URL to look
        // like a no-op restart in E2E tests.
        return;
      }
      // Offline hint: dismissed already → skip network to avoid flash.
      if (readHint() === "dismissed") return;
      const prog = await fetchProgress();
      if (cancelled) return;
      if (!prog || prog.exists === false) {
        setStep(0);
        setVisible(true);
        return;
      }
      if (prog.completed_at || prog.dismissed_at) {
        writeHint("dismissed");
        return;
      }
      setStep(Number(prog.step) || 0);
      setVisible(true);
    })();
    return () => { cancelled = true; };
  }, [force, restartParam, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!visible) return;
    const sel = STEPS[step]?.targetSelector;
    if (!sel) { setTargetRect(null); return; }
    const el = document.querySelector(sel);
    if (!el) { setTargetRect(null); return; }
    const r = el.getBoundingClientRect();
    setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [visible, step]);

  // Focus the dialog on show for keyboard users.
  useEffect(() => { if (visible) dialogRef.current?.focus(); }, [visible]);

  const dismiss = useCallback(async () => {
    setVisible(false);
    writeHint("dismissed");
    await postProgress({ dismissed: true });
    onClose?.();
  }, [onClose]);

  const complete = useCallback(async () => {
    setVisible(false);
    writeHint("dismissed");
    await postProgress({ completed: true, step: STEPS.length });
    onClose?.();
  }, [onClose]);

  const next = useCallback(async () => {
    if (step >= STEPS.length - 1) return complete();
    const newStep = step + 1;
    setStep(newStep);
    await postProgress({ step: newStep });
  }, [step, complete]);

  const prev = useCallback(async () => {
    if (step <= 0) return;
    const newStep = step - 1;
    setStep(newStep);
    await postProgress({ step: newStep });
  }, [step]);

  // Escape to dismiss; ArrowLeft/ArrowRight to navigate steps (R1b-α Fix #6).
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); dismiss(); return; }
      if (e.key === "ArrowRight") { e.preventDefault(); next(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, dismiss, next, prev]);

  if (!visible) return null;
  const s = STEPS[Math.max(0, Math.min(step, STEPS.length - 1))];
  const transition = prefersReducedMotion ? "none" : "opacity 0.15s ease";
  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="owner-tour-title"
      data-testid="owner-first-run-tour"
      style={{ position: "fixed", inset: 0, zIndex: 80, pointerEvents: "none" }}
    >
      <div style={{
        position: "absolute", inset: 0,
        background: targetRect ? "transparent" : "rgba(15, 15, 16, 0.55)",
        pointerEvents: "auto", transition,
      }} onClick={dismiss} />
      {targetRect && (
        <>
          <div style={{ position: "absolute", inset: 0, background: "rgba(15,15,16,0.55)", pointerEvents: "auto",
                        clipPath: `polygon(
                          0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                          ${targetRect.left - 4}px ${targetRect.top - 4}px,
                          ${targetRect.left - 4}px ${targetRect.top + targetRect.height + 4}px,
                          ${targetRect.left + targetRect.width + 4}px ${targetRect.top + targetRect.height + 4}px,
                          ${targetRect.left + targetRect.width + 4}px ${targetRect.top - 4}px,
                          ${targetRect.left - 4}px ${targetRect.top - 4}px
                        )` }}
               onClick={dismiss} />
          <div style={{
            position: "absolute",
            top: targetRect.top - 4, left: targetRect.left - 4,
            width: targetRect.width + 8, height: targetRect.height + 8,
            border: "2px solid #B08D57", borderRadius: 8, boxShadow: "0 0 0 4px rgba(176,141,87,0.25)",
            pointerEvents: "none",
          }} />
        </>
      )}

      <div
        ref={dialogRef}
        tabIndex={-1}
        data-testid={s.testid}
        data-tour-step-key={s.key}
        style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 480, background: "#1A1A1C", color: "#FAFAF7",
          border: "1px solid #3A3A3D", borderRadius: 12,
          padding: "28px 30px", pointerEvents: "auto",
          boxShadow: "0 20px 70px rgba(0,0,0,0.5)",
          fontFamily: '"Inter Tight", system-ui, sans-serif',
          outline: "none",
        }}
      >
        <div style={{
          fontFamily: '"JetBrains Mono", monospace', fontSize: 10,
          letterSpacing: "0.18em", textTransform: "uppercase",
          color: "#B08D57", marginBottom: 8,
        }}>Product tour · {step + 1} of {STEPS.length}</div>
        <h3 id="owner-tour-title" style={{
          fontFamily: '"Fraunces", Georgia, serif', fontSize: 22, fontWeight: 600,
          margin: "0 0 10px 0",
        }}>{s.title}</h3>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: "#A8A497", margin: "0 0 22px 0" }}>
          {s.body}
        </p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            data-testid="owner-tour-dismiss"
            onClick={dismiss}
            style={{
              background: "transparent", border: 0, color: "#A8A497",
              fontFamily: "inherit", fontSize: 13, cursor: "pointer", padding: "0 8px",
              minHeight: 44, minWidth: 44,
            }}
          >Skip tour</button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button
                type="button"
                data-testid="owner-tour-prev"
                onClick={prev}
                style={{
                  background: "#242426", color: "#FAFAF7",
                  border: "1px solid #3A3A3D", borderRadius: 6,
                  padding: "8px 14px", fontFamily: "inherit", fontSize: 13,
                  cursor: "pointer", minHeight: 44, minWidth: 44,
                }}
              >Back</button>
            )}
            <button
              type="button"
              data-testid="owner-tour-next"
              onClick={next}
              style={{
                background: "#B08D57", color: "#0F0F10",
                border: 0, borderRadius: 6, padding: "8px 16px",
                fontFamily: "inherit", fontSize: 13, fontWeight: 500,
                cursor: "pointer", minHeight: 44, minWidth: 44,
              }}
            >{step === STEPS.length - 1 ? "Got it" : "Next"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
